import React from "react";
import { PropertyValuationData } from "../../../../types/valuation";
import { PhotoGallery } from "../PhotoGallery";
import { formatCurrency } from "../../../../utils/formatters";
import { Bed, Bath, Ruler, ArrowLeft } from "lucide-react";
import { generatePropertyReport } from "../../../../services/reportService";
import { useTenant } from "../../../../contexts/TenantContext";

interface PropertyHeaderProps {
  propertyData: PropertyValuationData;
  colors: {
    text: string;
    primary: string;
    accent: string;
    background: string;
  };
  metrics: {
    valueChangePercent: string;
  };
  onNavigateBack: () => void;
}

export const PropertyHeader: React.FC<PropertyHeaderProps> = ({
  propertyData,
  colors,
  metrics,
  onNavigateBack,
}) => {
  const { config } = useTenant();
  return (
    <div className="bg-gray-50 border-b-2" style={{ borderColor: "#e5e7eb" }}>
      <div className="w-full lg:container mx-auto px-4 py-10  lg:px-12 lg:py-16">
        <button
          className="flex items-center text-sm lg:text-base py-1.5 px-3 lg:px-4 lg:py-2 bg-white backdrop-blur-sm rounded-full shadow-xl text-gray-800 hover:text-gray-600 transition-all duration-300 min-h-touch min-w-touch mb-4"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = "/valuation";
          }}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Valuation
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10  xl:gap-20">
          <div>
            <PhotoGallery property={propertyData} />
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex flex-col lg:flex-row justify-between items-start  gap-5 lg:gap-1">
              <div>
                <h1 className="text-lg md:text-2xl 2xl:text-3xl font-bold text-gray-900">
                  Property Value Report
                </h1>
                <p className="mt-2 text-base lg:text-xl text-gray-800 font-medium">
                  {propertyData?.propertyInfo?.address?.address ||
                    "Address not available"}
                </p>
                <p className="text-gray-500">
                  {propertyData?.propertyInfo?.address?.city &&
                  propertyData?.propertyInfo?.address?.state
                    ? `${propertyData.propertyInfo.address.city}, ${
                        propertyData.propertyInfo.address.state
                      } ${propertyData.propertyInfo.address.zip || ""}`
                    : "Location not available"}
                </p>
              </div>
              <div className="lg:text-right">
                <div className="text-sm text-gray-600">Current Value</div>
                <div
                  className="text-2xl xl:text-4xl font-extrabold"
                  style={{ color: colors.primary }}
                >
                  {formatCurrency(propertyData.estimatedValue)}
                </div>
                {metrics && (
                  <div
                    className="text-sm lg:text-base"
                    style={{
                      color:
                        Number(metrics.valueChangePercent) >= 0
                          ? colors.primary
                          : colors.accent,
                    }}
                  >
                    {metrics.valueChangePercent}% since purchase
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-2 lg:gap-4">
              <div
                className="flex flex-col items-center bg-white rounded-lg px-3 py-4 border"
                style={{ borderColor: `${colors.primary}80` }}
              >
                <p className="text-sm text-gray-500 mb-2">Bedrooms</p>
                <div className="flex items-center gap-2">
                  <Bed className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                  <p className="text-lg lg:text-xl font-bold text-gray-900">
                    {propertyData.propertyInfo?.bedrooms || "N/A"}
                  </p>
                </div>
              </div>
              <div
                className="flex flex-col items-center bg-white rounded-lg px-3 py-4 border"
                style={{ borderColor: `${colors.primary}80` }}
              >
                <p className="text-sm text-gray-500 mb-2">Bathrooms</p>
                <div className="flex items-center gap-2">
                  <Bath className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                  <p className="text-lg lg:text-xl  font-bold text-gray-900">
                    {propertyData.propertyInfo?.bathrooms || "N/A"}
                  </p>
                </div>
              </div>
              <div
                className="flex flex-col items-center bg-white rounded-lg px-3 py-4 border"
                style={{ borderColor: `${colors.primary}80` }}
              >
                <p className="text-sm text-gray-500 mb-2">Square Feet</p>
                <div className="flex items-center gap-2">
                  <Ruler className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                  <p className="text-lg lg:text-xl  font-bold text-gray-900">
                    {propertyData.propertyInfo?.livingSquareFeet?.toLocaleString() ||
                      "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex space-x-4">
              <button
                className="text-white px-4 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: colors.primary }}
                onClick={() => {
                  if (!config) return;
                  generatePropertyReport(propertyData, {
                    colors: {
                      primary: colors.primary,
                      text: colors.text,
                    },
                    tenant: config,
                  });
                }}
                onMouseOver={(e) => {
                  (
                    e.target as HTMLButtonElement
                  ).style.backgroundColor = `${colors.primary}dd`;
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor =
                    colors.primary;
                }}
              >
                Download Report
              </button>
              <a
                href={`https://www.google.com/maps?q=${encodeURIComponent(
                  propertyData.propertyInfo?.address?.address || ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: `${colors.background}20`,
                  color: colors.text,
                }}
              >
                View on Map
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
